/**
 * ONE AGREEMENT: what was agreed, the standing schedule it produces, the visits on the calendar
 * from it, and the four things that can be done to it.
 *
 * EVERY VISIT TAKEN OFF THE CALENDAR IS COUNTED BACK. Pausing, ending, moving to another weekday and
 * changing cadence each cancel future visits, and the server writes a change record with a typed
 * reason for every one of them, because each was a Thursday the client had been told about. So each
 * action here asks for that reason and, when it is done, says how many visits it took off.
 *
 * A cadence change is not an edit. Cadence sets the price, so it ends this agreement with
 * `superseded_cadence_change` and opens another; this screen then goes to the new one.
 *
 * NOR IS ANOTHER WEEKDAY (ops #76). The cadence and its price stay as they are, so the agreement stays
 * too: the standing schedule ends the day before the new day takes over and a second one opens on the
 * same agreement from its first clean, a paused one with its pause. The two days are kept apart, as a
 * cadence change keeps them, so no clean on the old day between them is left on the calendar.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AGREEMENT_STATE_LABELS,
  CADENCES,
  CADENCE_LABELS,
  ENDED_REASONS,
  ENDED_REASON_LABELS,
  PRICE_SOURCE_LABELS,
  SERVICE_LINE_LABELS,
  SILENCE_REASONS,
  SILENCE_REASON_LABELS,
  VISIT_KIND_LABELS,
  type Cadence,
  type EndedReason,
  type RateLine,
  type ServiceLine,
  type SilenceReason,
} from '@fk/shared';
import {
  ApiError,
  changeCadence,
  endAgreement,
  getAgreement,
  getCatalog,
  listCleaners,
  pauseSeries,
  reanchorSeries,
  resumeSeries,
  type AgreementDetail as AgreementDetailShape,
  type CleanerRow,
  type SeriesRow,
} from '../api.ts';
import { offeredForAClient } from '../checks.ts';
import { Card, Detail, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { Button, Field, SelectField, TextAreaField, TextField } from '../components/Field.tsx';
import { Busy, EmptyNotice, ErrorNotice, SavedNotice } from '../components/Notice.tsx';
import { VisitStatusPill, agreementStateTone } from '../components/StatusPills.tsx';
import {
  STATE,
  centsFromDollars,
  daysBetween,
  formatDay,
  formatInstant,
  formatWindow,
  isDay,
  isTimeOfDay,
  longDay,
  money,
  orNull,
  weekdayName,
} from '../format.ts';
import { RateLineChoice, describeSeries, resolveLine } from './Arrangements.tsx';

type EndReason = Exclude<EndedReason, 'superseded_cadence_change'>;
const END_REASONS = ENDED_REASONS.filter(
  (reason): reason is EndReason => reason !== 'superseded_cadence_change',
);

/**
 * A sentence carried from the page that opened this one. The cadence change creates a new
 * agreement and navigates here, so the count of visits it moved is handed over in the route state
 * rather than set on a form that has just been unmounted.
 */
function carriedNotice(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  const saved = (state as { saved?: unknown }).saved;
  return typeof saved === 'string' && saved.length > 0 ? saved : null;
}

export function AgreementDetail({ today }: { today: string }) {
  const { id = '' } = useParams();
  const route = useLocation();
  const carried = carriedNotice(route.state);
  const [detail, setDetail] = useState<AgreementDetailShape | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [cleaners, setCleaners] = useState<readonly CleanerRow[] | null>(null);
  const [lines, setLines] = useState<readonly RateLine[] | null>(null);
  const [saved, setSaved] = useState<string | null>(carried);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  // Going from one agreement to another keeps this component mounted, so the previous agreement
  // is cleared by hand and the notice is whatever the new route carried, if anything.
  useEffect(() => {
    setDetail(null);
    setSaved(carried);
  }, [id, carried]);

  useEffect(() => {
    let live = true;
    setError(null);
    getAgreement(id)
      .then((answer) => {
        if (live) setDetail(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'That arrangement did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [id, attempt]);

  useEffect(() => {
    let live = true;
    listCleaners()
      .then((answer) => {
        if (live) setCleaners(answer.cleaners);
      })
      .catch(() => {
        // Names are decoration on this screen; an id is shown instead when the crew did not load.
      });
    getCatalog()
      .then((answer) => {
        if (live) setLines(answer.lines);
      })
      .catch(() => {
        // Only the cadence change needs the card, and it says so when the card is missing.
      });
    return () => {
      live = false;
    };
  }, []);

  const back = (
    <Link
      to="/clients"
      className="mb-3 inline-flex min-h-11 desk:min-h-9 items-center text-sm font-medium text-navy/70"
    >
      Back to clients
    </Link>
  );

  if (error) {
    return (
      <div>
        {back}
        <ErrorNotice
          title="That arrangement did not load"
          message={error.message}
          onRetry={reload}
        />
      </div>
    );
  }
  if (!detail) {
    return (
      <div>
        {back}
        <Busy what="this arrangement" />
      </div>
    );
  }

  const { agreement, series, visits, home } = detail;
  const live = series.find((row) => row.endedOn === null) ?? null;
  const isLive = agreement.state === 'active' || agreement.state === 'paused';

  return (
    <div>
      {home ? (
        <Link
          to={`/clients/${home.clientId}`}
          className="mb-3 inline-flex min-h-11 desk:min-h-9 items-center text-sm font-medium text-navy/70"
        >
          Back to the household
        </Link>
      ) : (
        back
      )}

      <ScreenTitle
        title={`${SERVICE_LINE_LABELS[agreement.serviceLine]}, ${CADENCE_LABELS[agreement.cadence].toLowerCase()}`}
        subtitle={home ? `${home.addressLine1}, ${home.town}` : undefined}
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <Pill tone={agreementStateTone(agreement.state)}>
          {AGREEMENT_STATE_LABELS[agreement.state]}
        </Pill>
        <Pill tone="gold">{money(agreement.priceCents)} a visit</Pill>
      </div>

      {saved ? (
        <div className="mb-4">
          <SavedNotice>{saved}</SavedNotice>
        </div>
      ) : null}

      <Card className="mb-4">
        <h2 className="mb-2 font-serif text-lg">What was agreed</h2>
        <dl>
          <Detail label="Service" value={SERVICE_LINE_LABELS[agreement.serviceLine]} />
          <Detail label="Cadence" value={CADENCE_LABELS[agreement.cadence]} />
          <Detail
            label="Price"
            value={`${money(agreement.priceCents)}, ${PRICE_SOURCE_LABELS[agreement.priceSource].toLowerCase()}`}
          />
          <Detail label="Card line" value={agreement.rateLineCode} />
          <Detail label="Started" value={formatDay(agreement.startedOn)} />
          <Detail
            label="Ended"
            value={
              agreement.endedOn
                ? `${formatDay(agreement.endedOn)}${agreement.endedReason ? `, ${ENDED_REASON_LABELS[agreement.endedReason].toLowerCase()}` : ''}`
                : null
            }
          />
          <Detail
            label="Deep clean rule said"
            value={formatInstant(agreement.deepCleanDisclosedAt)}
          />
          <Detail label="Access settled" value={formatInstant(agreement.accessConfirmedAt)} />
        </dl>
      </Card>

      {home ? (
        <Card className="mb-4">
          <h2 className="mb-2 font-serif text-lg">The home</h2>
          <dl>
            <Detail
              label="Address"
              value={[
                home.label,
                home.addressLine1,
                home.addressLine2,
                `${home.town}, ${STATE} ${home.postalCode}`,
              ]
                .filter(Boolean)
                .join('\n')}
            />
          </dl>
        </Card>
      ) : null}

      <Card className="mb-4">
        <h2 className="mb-2 font-serif text-lg">The standing schedule</h2>
        {series.length === 0 ? (
          <p className="text-sm text-navy/70">A one-off, with no standing schedule.</p>
        ) : (
          <ul className="space-y-2">
            {series.map((row) => (
              <li key={row.id}>
                <p className="text-sm font-medium">{describeSeries(row, cleaners)}</p>
                <dl>
                  <Detail label="First visit" value={formatDay(row.anchorDate)} />
                  <Detail
                    label="Paused"
                    value={
                      row.pausedFrom
                        ? `From ${formatDay(row.pausedFrom)}${row.pausedUntil ? ` to ${formatDay(row.pausedUntil)}` : ', no return date yet'}`
                        : null
                    }
                  />
                  <Detail
                    label="Slot held until"
                    value={row.holdSlotUntil ? formatDay(row.holdSlotUntil) : null}
                  />
                  <Detail label="Written ahead to" value={formatDay(row.materialisedTo)} />
                  <Detail label="Ended" value={formatDay(row.endedOn)} />
                </dl>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <h2 className="mb-2 font-serif text-xl">Coming up</h2>
      {visits.length === 0 ? (
        <EmptyNotice title="No visits on the calendar from today">
          <p>
            {agreement.state === 'ended'
              ? 'This arrangement has ended.'
              : live?.pausedFrom
                ? 'The schedule is paused. Resume it below to fill the calendar in again.'
                : 'The nightly run writes visits about twelve weeks ahead.'}
          </p>
        </EmptyNotice>
      ) : (
        <ul className="mb-6 space-y-2">
          {visits.map((visit) => (
            <li key={visit.id}>
              <Link
                to={`/visits/${visit.id}`}
                className="flex min-h-12 desk:min-h-9 flex-wrap items-center justify-between gap-2 rounded-lg border border-navy/15 bg-parchment px-3 py-2"
              >
                {/* A LINK'S NAME IS ITS TEXT RUN TOGETHER, so the four parts a sighted reader tells
                    apart by weight, colour and a pill's border read "September 138:00 am" without
                    the hidden commas. The day stays a text node of its own, because `visitOnDay`
                    in `scripts/walk/config.mjs` finds the visit the walk booked by it. */}
                <span className="text-sm font-medium">
                  {longDay(visit.scheduledDate)}
                  <span className="sr-only">, </span>
                  <span className="ml-2 font-normal text-navy/70">
                    {formatWindow(visit.windowStart, visit.windowEnd) ?? 'no window'}
                  </span>
                  <span className="sr-only">, </span>
                </span>
                <span className="flex gap-2">
                  <Pill>{VISIT_KIND_LABELS[visit.kind]}</Pill>
                  <span className="sr-only">, </span>
                  <VisitStatusPill status={visit.status} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {isLive && live ? (
        live.pausedFrom ? (
          <ResumeForm
            series={live}
            today={today}
            onDone={(text) => {
              setSaved(text);
              reload();
            }}
          />
        ) : (
          <PauseForm
            series={live}
            today={today}
            onDone={(text) => {
              setSaved(text);
              reload();
            }}
          />
        )
      ) : null}

      {isLive && live && agreement.cadence !== 'one_time' ? (
        <ReanchorForm
          series={live}
          today={today}
          onDone={(text) => {
            setSaved(text);
            reload();
          }}
        />
      ) : null}

      {isLive ? (
        <CadenceForm
          agreementId={agreement.id}
          serviceLine={agreement.serviceLine}
          currentCadence={agreement.cadence}
          series={live}
          home={home}
          lines={lines}
          cleaners={cleaners ?? []}
        />
      ) : null}

      {agreement.state !== 'ended' ? (
        <EndForm
          agreementId={agreement.id}
          today={today}
          onDone={(text) => {
            setSaved(text);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The three actions.
 * ------------------------------------------------------------------ */

function Problems({ items }: { items: readonly string[] }) {
  if (items.length === 0) return null;
  return (
    <div
      className="mb-3 rounded-xl border border-coral/50 border-l-4 border-l-coral bg-parchment p-3"
      role="alert"
    >
      <ul className="list-disc space-y-1 pl-5 text-sm">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function TimeInput({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field id={id} label={label}>
      <input
        id={id}
        type="time"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 block min-h-11 desk:min-h-9 w-full rounded-lg border border-navy/25 bg-parchment px-3 py-2 text-navy"
      />
    </Field>
  );
}

function visitsWord(count: number): string {
  return count === 1 ? '1 visit was' : `${count} visits were`;
}

function PauseForm({
  series,
  today,
  onDone,
}: {
  series: SeriesRow;
  today: string;
  onDone: (text: string) => void;
}) {
  const [pausedFrom, setPausedFrom] = useState(today);
  const [pausedUntil, setPausedUntil] = useState('');
  const [holdSlotUntil, setHoldSlotUntil] = useState('');
  const [silenceReason, setSilenceReason] = useState<SilenceReason | ''>('');
  const [note, setNote] = useState('');
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFailure(null);
    const found: string[] = [];
    if (!isDay(pausedFrom)) found.push('A pause starts on a day.');
    if (pausedUntil && (!isDay(pausedUntil) || pausedUntil < pausedFrom)) {
      found.push('A pause cannot end before it begins.');
    }
    if (holdSlotUntil && !isDay(holdSlotUntil)) found.push('The slot is held until a day.');
    if (!silenceReason) found.push('Say how they know about the visits coming off the calendar.');
    setProblems(found);
    if (found.length > 0 || !silenceReason) return;
    setSaving(true);
    try {
      const result = await pauseSeries(series.id, {
        pausedFrom,
        pausedUntil: orNull(pausedUntil),
        holdSlotUntil: orNull(holdSlotUntil),
        silenceReason,
        note: orNull(note),
      });
      onDone(
        `Paused. ${visitsWord(result.visitsCancelled)} taken off the calendar, each with its record.`,
      );
    } catch (thrown) {
      setFailure(thrown instanceof ApiError ? thrown.message : 'The pause was not recorded.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <details className="mb-4 rounded-xl border border-navy/12 bg-parchment p-4">
      <summary className="min-h-11 desk:min-h-9 cursor-pointer font-serif text-lg">
        Pause the schedule
      </summary>
      <form className="mt-3" onSubmit={submit} noValidate>
        {failure ? (
          <div className="mb-3">
            <ErrorNotice title="Not recorded" message={failure} />
          </div>
        ) : null}
        <Problems items={problems} />
        <TextField
          id="pause-from"
          label="Paused from"
          type="date"
          required
          value={pausedFrom}
          onChange={setPausedFrom}
        />
        <TextField
          id="pause-until"
          label="Paused until"
          type="date"
          value={pausedUntil}
          onChange={setPausedUntil}
          hint="Leave empty for a pause with no agreed return date, which reads differently from ended."
        />
        <TextField
          id="pause-hold"
          label="Hold their slot until"
          type="date"
          value={holdSlotUntil}
          onChange={setHoldSlotUntil}
          hint="How long before the slot can honestly be offered to somebody else."
        />
        <SelectField
          id="pause-reason"
          label="How they know"
          required
          value={silenceReason}
          onChange={setSilenceReason}
          options={SILENCE_REASONS}
          toLabel={(value) => SILENCE_REASON_LABELS[value]}
          placeholder="Choose one"
          hint="Every visit taken off the calendar gets a record with this reason on it."
        />
        <TextAreaField id="pause-note" label="Note" value={note} onChange={setNote} rows={2} />
        <Button type="submit" full disabled={saving}>
          {saving ? 'Recording' : 'Pause it'}
        </Button>
      </form>
    </details>
  );
}

function ResumeForm({
  series,
  today,
  onDone,
}: {
  series: SeriesRow;
  today: string;
  onDone: (text: string) => void;
}) {
  const [resumeOn, setResumeOn] = useState(series.pausedUntil ?? today);
  const [silenceReason, setSilenceReason] = useState<SilenceReason | ''>('');
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Later than the pause was set to end: the visits planned in between come off the calendar, each
  // with a record, so the person says how she knows.
  const extending = series.pausedUntil !== null && isDay(resumeOn) && resumeOn > series.pausedUntil;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFailure(null);
    if (!isDay(resumeOn)) {
      setFailure('Cleans resume on a day.');
      return;
    }
    if (extending && !silenceReason) {
      setFailure('Say how they know the visits before their return are off.');
      return;
    }
    setSaving(true);
    try {
      const result = await resumeSeries(series.id, {
        resumeOn,
        silenceReason: extending ? silenceReason || null : null,
      });
      const count = result.materialised.visitsWritten;
      const off = result.materialised.visitsCancelled;
      onDone(
        `Resumed. ${count === 1 ? '1 visit is' : `${count} visits are`} back on the calendar.` +
          (off > 0
            ? ` ${off === 1 ? '1 visit' : `${off} visits`} inside the longer pause came off it.`
            : ''),
      );
    } catch (thrown) {
      setFailure(thrown instanceof ApiError ? thrown.message : 'The resume was not recorded.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <details className="mb-4 rounded-xl border border-navy/12 bg-parchment p-4" open>
      <summary className="min-h-11 desk:min-h-9 cursor-pointer font-serif text-lg">
        Resume the schedule
      </summary>
      <form className="mt-3" onSubmit={submit} noValidate>
        {failure ? (
          <div className="mb-3">
            <ErrorNotice title="Not recorded" message={failure} />
          </div>
        ) : null}
        <TextField
          id="resume-on"
          label="Cleans resume on"
          type="date"
          required
          value={resumeOn}
          onChange={setResumeOn}
          hint="The next slot on or after this day is written back in, on the same weekday as before."
        />
        {extending ? (
          <SelectField
            id="resume-reason"
            label="How they know"
            required
            value={silenceReason}
            onChange={setSilenceReason}
            options={SILENCE_REASONS}
            toLabel={(value) => SILENCE_REASON_LABELS[value]}
            placeholder="Choose one"
            hint="They are back later than planned. The visits in between come off the calendar, each with a record carrying this reason."
          />
        ) : null}
        <Button type="submit" full disabled={saving}>
          {saving ? 'Recording' : 'Resume it'}
        </Button>
      </form>
    </details>
  );
}

/**
 * ANOTHER WEEKDAY AT THE SAME CADENCE (ops #76, `POST /api/series/:id/reanchor`). Two days, kept apart:
 * the day the old weekday stops holding, today or later, and the first clean on the new one, on or
 * after it and never a day the schedule already has. Every clean from the first day on comes off the
 * calendar with its record and the reason typed, and the new day is written in. A paused schedule
 * moves with its pause, and resuming ends it as before. The server holds every rule again, a change
 * still waiting to be told among them, and its sentence is shown as it comes back.
 */
function ReanchorForm({
  series,
  today,
  onDone,
}: {
  series: SeriesRow;
  today: string;
  onDone: (text: string) => void;
}) {
  const [fromDate, setFromDate] = useState('');
  const [anchorDate, setAnchorDate] = useState('');
  const [windowStart, setWindowStart] = useState(series.windowStartLocal);
  const [windowEnd, setWindowEnd] = useState(series.windowEndLocal);
  const [silenceReason, setSilenceReason] = useState<SilenceReason | ''>('');
  const [note, setNote] = useState('');
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFailure(null);
    const found: string[] = [];
    if (!isDay(fromDate)) found.push('The new day takes over from a day.');
    else if (fromDate < today) {
      found.push('The new day cannot take over from a day already gone. Make it today or later.');
    }
    if (!isDay(anchorDate)) found.push('Pick the first clean on the new day.');
    else if (isDay(fromDate) && anchorDate < fromDate) {
      found.push('The first clean on the new day cannot be before the day it takes over from.');
    } else if ((daysBetween(series.anchorDate, anchorDate) ?? 1) % series.intervalDays === 0) {
      found.push(
        'That is a day the schedule already has. A clean already on the calendar moves on its own page.',
      );
    }
    if (!isTimeOfDay(windowStart) || !isTimeOfDay(windowEnd)) {
      found.push('The arrival window needs a start and an end time.');
    } else if (windowStart >= windowEnd) {
      found.push('The arrival window has to end after it starts.');
    }
    if (!silenceReason) found.push('Say how they know about the visits coming off the calendar.');
    setProblems(found);
    if (found.length > 0 || !silenceReason) return;
    setSaving(true);
    try {
      const result = await reanchorSeries(series.id, {
        fromDate,
        anchorDate,
        windowStartLocal: windowStart,
        windowEndLocal: windowEnd,
        silenceReason,
        note: orNull(note),
      });
      onDone(
        `Moved to ${weekdayName(anchorDate)}s. ${visitsWord(result.visitsCancelled)} taken off the calendar, each with its record, and ${result.materialised.visitsWritten} written on the new day.`,
      );
    } catch (thrown) {
      setFailure(thrown instanceof ApiError ? thrown.message : 'The move was not recorded.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <details className="mb-4 rounded-xl border border-navy/12 bg-parchment p-4">
      <summary className="min-h-11 desk:min-h-9 cursor-pointer font-serif text-lg">
        Move to another weekday
      </summary>
      <p className="mt-2 text-xs text-navy/65">
        The cadence and its price stay as they are. Every clean from the day the new one takes over
        comes off the calendar with a record, and the new day is written in.
      </p>
      {series.pausedFrom ? (
        <p className="mt-2 text-sm">
          Their cleans are paused from {formatDay(series.pausedFrom)}
          {series.pausedUntil
            ? ` to ${formatDay(series.pausedUntil)}`
            : ', with no return date yet'}
          : the new day keeps the pause, and resuming ends it as before.
        </p>
      ) : null}
      <form className="mt-3" onSubmit={submit} noValidate>
        {failure ? (
          <div className="mb-3">
            <ErrorNotice title="Not recorded" message={failure} />
          </div>
        ) : null}
        <Problems items={problems} />
        <TextField
          id="reanchor-from"
          label="The new day takes over from"
          type="date"
          required
          value={fromDate}
          onChange={setFromDate}
          hint="The first day the old one no longer holds. Every clean from it on comes off the calendar, the ones before the first clean on the new day included."
        />
        <TextField
          id="reanchor-anchor"
          label="First clean on the new day"
          type="date"
          required
          value={anchorDate}
          onChange={setAnchorDate}
          hint={
            isDay(anchorDate) ? `Every clean after it is a ${weekdayName(anchorDate)}.` : undefined
          }
        />
        <div className="grid grid-cols-2 gap-3">
          <TimeInput
            id="reanchor-start"
            label="Arrives from"
            value={windowStart}
            onChange={setWindowStart}
          />
          <TimeInput
            id="reanchor-end"
            label="Arrives by"
            value={windowEnd}
            onChange={setWindowEnd}
          />
        </div>
        <SelectField
          id="reanchor-reason"
          label="How they know"
          required
          value={silenceReason}
          onChange={setSilenceReason}
          options={SILENCE_REASONS}
          toLabel={(value) => SILENCE_REASON_LABELS[value]}
          placeholder="Choose one"
          hint="Every clean taken off the old day gets a record with this reason on it."
        />
        <TextAreaField id="reanchor-note" label="Note" value={note} onChange={setNote} rows={2} />
        <Button type="submit" full disabled={saving}>
          {saving ? 'Recording' : 'Move it'}
        </Button>
      </form>
    </details>
  );
}

function CadenceForm({
  agreementId,
  serviceLine,
  currentCadence,
  series,
  home,
  lines,
  cleaners,
}: {
  agreementId: string;
  /** The server prices the new agreement from the old one's service line, so the choice matches it. */
  serviceLine: ServiceLine;
  currentCadence: Cadence;
  series: SeriesRow | null;
  home: { bedrooms: number | null; bathrooms: number | null } | null;
  lines: readonly RateLine[] | null;
  cleaners: readonly CleanerRow[];
}) {
  const navigate = useNavigate();
  const options = CADENCES.filter((value) => value !== 'one_time' && value !== currentCadence);
  const [cadence, setCadence] = useState<Cadence | ''>('');
  const [chosenCode, setChosenCode] = useState('');
  const [quotedDollars, setQuotedDollars] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [anchorDate, setAnchorDate] = useState('');
  const [windowStart, setWindowStart] = useState(series?.windowStartLocal ?? '09:00');
  const [windowEnd, setWindowEnd] = useState(series?.windowEndLocal ?? '13:00');
  const [cleanerId, setCleanerId] = useState(series?.cleanerId ?? '');
  const [silenceReason, setSilenceReason] = useState<SilenceReason | ''>('');
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Active and carrying the owners' checks mark, or the cleaner the schedule already has (ops #85).
  const active = cleaners.filter((cleaner) => offeredForAClient(cleaner, series?.cleanerId));
  const shape = home ?? { bedrooms: null, bathrooms: null };
  const resolved =
    lines && cadence ? resolveLine(lines, serviceLine, cadence, shape, chosenCode) : null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFailure(null);
    const found: string[] = [];
    if (!cadence) found.push('Choose the new cadence.');
    if (!lines) found.push('The published card has not loaded, so the new price cannot be read.');
    if (lines && cadence && !resolved?.line)
      found.push('Choose the line of the card this is priced from.');
    if (resolved?.line?.priceCents === null && centsFromDollars(quotedDollars) === null) {
      found.push('Type the figure that was quoted after the walkthrough, in dollars.');
    }
    if (!isDay(fromDate)) found.push('The new cadence starts on a day.');
    if (!isDay(anchorDate)) found.push('Pick the first visit under the new cadence.');
    else if (isDay(fromDate) && anchorDate < fromDate) {
      found.push('The first visit cannot be before the new cadence starts.');
    }
    if (!isTimeOfDay(windowStart) || !isTimeOfDay(windowEnd)) {
      found.push('The arrival window needs a start and an end time.');
    } else if (windowStart >= windowEnd)
      found.push('The arrival window has to end after it starts.');
    if (!silenceReason) found.push('Say how they know about the visits coming off the calendar.');
    setProblems(found);
    if (found.length > 0 || !cadence || !resolved?.line || !silenceReason) return;

    setSaving(true);
    try {
      const result = await changeCadence(agreementId, {
        cadence,
        fromDate,
        rateLineCode: resolved.line.code,
        quotedPriceCents:
          resolved.line.priceCents === null ? centsFromDollars(quotedDollars) : null,
        series: {
          anchorDate,
          windowStartLocal: windowStart,
          windowEndLocal: windowEnd,
          cleanerId: cleanerId || null,
        },
        silenceReason,
      });
      // The new agreement is the one to look at now. The count travels with the route and the new
      // page shows it, because this form is unmounted the moment the id changes.
      navigate(`/agreements/${result.agreementId}`, {
        state: {
          saved: `Cadence changed. ${visitsWord(result.visitsCancelled)} taken off the old schedule and ${result.materialised.visitsWritten} written on the new one.`,
        },
      });
    } catch (thrown) {
      setFailure(
        thrown instanceof ApiError ? thrown.message : 'The cadence change was not recorded.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <details className="mb-4 rounded-xl border border-navy/12 bg-parchment p-4">
      <summary className="min-h-11 desk:min-h-9 cursor-pointer font-serif text-lg">
        Change how often
      </summary>
      <p className="mt-2 text-xs text-navy/65">
        Cadence sets the price, so this ends the current agreement the day before and opens a new
        one priced from the card. Every visit from that day on is taken off with a record, and the
        new schedule is written in.
      </p>
      <form className="mt-3" onSubmit={submit} noValidate>
        {failure ? (
          <div className="mb-3">
            <ErrorNotice title="Not recorded" message={failure} />
          </div>
        ) : null}
        <Problems items={problems} />
        <SelectField
          id="cadence-new"
          label="New cadence"
          required
          value={cadence}
          onChange={(value) => {
            setCadence(value);
            setChosenCode('');
          }}
          options={options}
          toLabel={(value) => CADENCE_LABELS[value]}
          placeholder="Choose one"
        />
        {lines && cadence ? (
          <RateLineChoice
            idPrefix="cadence"
            lines={lines}
            serviceLine={serviceLine}
            cadence={cadence}
            home={shape}
            chosenCode={chosenCode}
            onChoose={setChosenCode}
            quotedDollars={quotedDollars}
            onQuoted={setQuotedDollars}
          />
        ) : null}
        <TextField
          id="cadence-from"
          label="New cadence starts"
          type="date"
          required
          value={fromDate}
          onChange={setFromDate}
          hint="The current agreement ends the day before."
        />
        <TextField
          id="cadence-anchor"
          label="First visit under it"
          type="date"
          required
          value={anchorDate}
          onChange={setAnchorDate}
          hint={
            isDay(anchorDate) ? `Every visit after it is a ${weekdayName(anchorDate)}.` : undefined
          }
        />
        <div className="grid grid-cols-2 gap-3">
          <TimeInput
            id="cadence-start"
            label="Arrives from"
            value={windowStart}
            onChange={setWindowStart}
          />
          <TimeInput
            id="cadence-end"
            label="Arrives by"
            value={windowEnd}
            onChange={setWindowEnd}
          />
        </div>
        <SelectField
          id="cadence-cleaner"
          label="Whose slot it is"
          value={cleanerId}
          onChange={setCleanerId}
          options={active.map((cleaner) => cleaner.id)}
          toLabel={(id) => active.find((cleaner) => cleaner.id === id)?.displayName ?? id}
          placeholder="Nobody yet"
        />
        <SelectField
          id="cadence-reason"
          label="How they know"
          required
          value={silenceReason}
          onChange={setSilenceReason}
          options={SILENCE_REASONS}
          toLabel={(value) => SILENCE_REASON_LABELS[value]}
          placeholder="Choose one"
          hint="Every visit taken off the old schedule gets a record with this reason on it."
        />
        <Button type="submit" full disabled={saving}>
          {saving ? 'Recording' : 'Change the cadence'}
        </Button>
      </form>
    </details>
  );
}

function EndForm({
  agreementId,
  today,
  onDone,
}: {
  agreementId: string;
  today: string;
  onDone: (text: string) => void;
}) {
  const [endedOn, setEndedOn] = useState(today);
  const [endedReason, setEndedReason] = useState<EndReason | ''>('');
  const [silenceReason, setSilenceReason] = useState<SilenceReason | ''>('');
  const [note, setNote] = useState('');
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFailure(null);
    const found: string[] = [];
    if (!isDay(endedOn)) found.push('An agreement ends on a day.');
    if (!endedReason) found.push('Say why it ended. The retention number depends on it.');
    if (!silenceReason) found.push('Say how they know about the visits coming off the calendar.');
    setProblems(found);
    if (found.length > 0 || !endedReason || !silenceReason) return;
    setSaving(true);
    try {
      const result = await endAgreement(agreementId, {
        endedOn,
        endedReason,
        silenceReason,
        note: orNull(note),
      });
      onDone(
        `Ended. ${visitsWord(result.visitsCancelled)} taken off the calendar, each with its record.`,
      );
    } catch (thrown) {
      setFailure(thrown instanceof ApiError ? thrown.message : 'The ending was not recorded.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <details className="mb-4 rounded-xl border border-navy/12 bg-parchment p-4">
      <summary className="min-h-11 desk:min-h-9 cursor-pointer font-serif text-lg">
        End the arrangement
      </summary>
      <form className="mt-3" onSubmit={submit} noValidate>
        {failure ? (
          <div className="mb-3">
            <ErrorNotice title="Not recorded" message={failure} />
          </div>
        ) : null}
        <Problems items={problems} />
        <TextField
          id="end-on"
          label="Ends on"
          type="date"
          required
          value={endedOn}
          onChange={setEndedOn}
        />
        <SelectField
          id="end-reason"
          label="Why"
          required
          value={endedReason}
          onChange={setEndedReason}
          options={END_REASONS}
          toLabel={(value) => ENDED_REASON_LABELS[value]}
          placeholder="Choose one"
        />
        <SelectField
          id="end-silence"
          label="How they know"
          required
          value={silenceReason}
          onChange={setSilenceReason}
          options={SILENCE_REASONS}
          toLabel={(value) => SILENCE_REASON_LABELS[value]}
          placeholder="Choose one"
          hint="Every visit taken off the calendar gets a record with this reason on it."
        />
        <TextAreaField id="end-note" label="Note" value={note} onChange={setNote} rows={2} />
        <Button type="submit" full disabled={saving}>
          {saving ? 'Recording' : 'End it'}
        </Button>
      </form>
    </details>
  );
}
